<template>
  <div class="notification-box">
    <div class="notification-box__border with-h-line-right" />

    <div class="notification__icon d-flex">
      <div class="notification__icon__wrapper">
        <div
          class="notification__icon__bg-circle"
          :style="circleStyle"
        >
          <w-icon :color="color">
            {{ icon }}
          </w-icon>
        </div>
      </div>

      <div 
        class="notification__content" 
        @click="redirectToNotificationsOverview"
      >
        <div class="notification__content__wrapper">
          <div class="notification__headline__wrapper">
            <w-row
              class="content"
              justify="space-between"
            >
              <w-column class="notification_infos">
                <w-row class="subject">
                  <h4>
                    {{ userInCCText
                    }}{{
                      startWithUpperCaseFormatter(notificationItem.data.subject)
                    }}
                  </h4>
                </w-row>
                <w-row class="introduction">
                  <span v-html="formatIntroduction(introduction)" />
                </w-row>
              </w-column>
              <w-column cols="auto">
                <w-row
                  class="timestamp"
                  justify="end"
                >
                  {{ timeStamp }}
                </w-row>
                <w-row justify="end">
                  <w-view-button @click.stop="setReadOn()">
                    <template #tooltip>
                      {{ $t('innerNotification.words.markAsRead') }}
                    </template>
                  </w-view-button>
                </w-row>
                <w-row justify="end">
                  <w-export-button
                    v-if="moduleLink != null && isLinkAllowed(moduleLink)"
                    @click.stop="navigateToMessageLink()"
                  >
                    <template #tooltip>
                      {{ moduleLinkName }}
                    </template>
                  </w-export-button>
                </w-row>
              </w-column>
            </w-row>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// service
import notificationService from '@/modules/notifications/services/notification-service';
import notificationHelperService from '@/modules/notifications/services/notification-helper-service';
import dateFormatter from '@/services/formatter_services/date-formatter';
import { htmlDecode } from '@/services/parsing_services/html-decode-service';

export default {
  name: 'WorkNotificationItem',
  props: {
    notificationItem: { type: Object, required: true },
  },
  data() {
    return {
      color: 'warning',
    };
  },
  computed: {
    circleStyle() {
      let color = `var(--v-${this.color}-lighten3)`;
      return `background-color: ${color}; border-color: ${color}`;
    },
    timeStamp() {
      return dateFormatter.formatDateToLocaleDateStringMessages(
        this.notificationItem.data.createdAt
      );
    },
    introduction() {
      let intro = this.notificationItem.data.introduction;
      return intro.length > 250 ? intro.substring(0, 250) + '...' : intro;
    },
    userInCCText() {
      if (this.notificationItem.data.recipientInCC) return 'In CC: ';
      else return '';
    },
    moduleLinkName() {
      return notificationHelperService.moduleLinkName(this.notificationItem);
    },
    moduleLink() {
      return notificationHelperService.moduleLink(this.notificationItem);
    },
    icon() {
      return notificationHelperService.icon(this.moduleLink);
    },
  },
  methods: {
    htmlDecode: htmlDecode,
    async setReadOn() {
      await notificationService.setRead(this.notificationItem.id);
      this.$emit('reload');
    },
    isLinkAllowed(link) {
      return notificationHelperService.isLinkAllowed(link);
    },
    navigateToMessageLink() {
      this.setReadOn();
      if(this.moduleLink.includes('messages')) {
        this.$router.push({ path: this.moduleLink + '/answer' });
      } else {
        this.$router.push({ path: this.moduleLink });
      }
    },
    redirectToNotificationsOverview() {
      this.$router.push({
        name: 'NotificationsOverview',
        params: { openDialogId: this.notificationItem.id },
      });
    },
    startWithUpperCaseFormatter(text) {
      return text?.charAt(0).toUpperCase() + text?.slice(1);
    },
    formatIntroduction(introduction) {
      return this.startWithUpperCaseFormatter(this.htmlDecode(introduction));
    }
  },
};
</script>

<style lang="scss" scoped>
$default-spacing: $half-grid-gutter;

.notification-box {
  position: relative;

  .notification-box__border {
    height: 100%;
    left: calc(#{$grid-gutter} + #{$default-spacing});
    position: absolute;
    top: 0;
    width: #{$slider-line__thickness--inactive};
    z-index: -1;
  }
}

.notification-box .notification__icon__wrapper {
  margin-right: #{$default-spacing};
  border-top: #{$default-spacing} solid var(--v-work_card-base);
  border-bottom: #{$default-spacing} solid var(--v-work_card-base);
  height: fit-content;

  .notification__icon__bg-circle {
    border: calc(#{$grid-gutter} - #{$spacer}) solid;
    border-radius: 50%;
  }
}

.notification-box .notification__content {
  display: inline-flex;
  margin-top: #{$grid-gutter};
  margin-left: #{$default-spacing};
  flex-grow: 1;

  .notification__content__wrapper {
    flex-grow: 1;
  }

  .notification__headline__wrapper {
    line-height: #{$w-h4-font-size};

    .content {
      margin-bottom: #{$default-spacing};

      .notification_infos {
        cursor: pointer;
        border-radius: $border-radius-root;
        padding: 0.5rem 1rem;
        &:hover {
          background-color: $default-hover-color !important;
        }
      }

      .timestamp {
        @include w-p4(false);
        color: $input-read-only-color;
        text-align: right;
      }

      .introduction {
        @include w-p4(false);
        color: $input-read-only-color;
        margin-top: #{$default-spacing};
        margin-bottom: #{$default-spacing};
      }
    }
  }
}
</style>
